import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import { Label } from "@/components/ui/label";
import * as L from "lucide-react";

export function CalendarScreen({ user, refresh, go }) {
  const [url, setUrl] = useState(user?.calendar_ical_url || "");
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState("");
  const [briefBusy, setBriefBusy] = useState(false);
  const [gcalStatus, setGcalStatus] = useState({ connected: false });
  const [gcalBusy, setGcalBusy] = useState(false);

  const loadGcalStatus = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/status");
      setGcalStatus(data);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { loadGcalStatus(); }, []);

  const connectGoogleCalendar = async () => {
    setGcalBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/authorize");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error("Couldn't start Google Calendar");
      setGcalBusy(false);
    }
  };
  const disconnectGoogleCalendar = async () => {
    setGcalBusy(true);
    try {
      await api.post("/oauth/calendar/disconnect");
      await refresh?.();
      await loadGcalStatus();
      toast.success("Google Calendar disconnected");
    } catch { toast.error("Couldn't disconnect"); }
    finally { setGcalBusy(false); }
  };

  const load = async () => {
    if (!user?.calendar_ical_url) return;
    setLoading(true);
    try {
      const { data } = await api.get("/calendar/upcoming");
      setEvents(data.events || []);
    } catch (e) { toast.error("Couldn't read calendar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.calendar_ical_url]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/settings", { calendar_ical_url: url });
      await refresh?.();
      toast.success(url ? "Calendar connected" : "Calendar disconnected");
    } catch (e) {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  const doBrief = async () => {
    setBriefBusy(true); setBrief("");
    try {
      const { data } = await api.post("/calendar/brief");
      setBrief(data.brief);
    } catch { toast.error("Brief failed"); }
    finally { setBriefBusy(false); }
  };

  const groups = groupByDay(events);

  return (
    <div className="p-5 space-y-4" data-testid="screen-calendar">
      <div>
        <div className="text-2xl font-display">Calendar</div>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
          Connect your calendar so Rammy can plan around your day. Google Calendar is recommended — Rammy can also draft events for your approval.
        </p>
      </div>

      {/* Google Calendar OAuth — the recommended path */}
      <div className={`rounded-2xl p-4 border ${gcalStatus.connected ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10" : gcalStatus.status === "expired" ? "border-amber-300 bg-amber-50" : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"}`} data-testid="gcal-card">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-[var(--rm-surface)] shadow-sm flex items-center justify-center shrink-0">
            <svg width="18" height="18" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="1" y="3" width="20" height="18" rx="3" fill="#4285F4"/>
              <rect x="1" y="3" width="20" height="6" rx="3" fill="#4285F4"/>
              <text x="11" y="17" textAnchor="middle" fontSize="10" fontFamily="Arial" fontWeight="700" fill="white">15</text>
            </svg>
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">Google Calendar</span>
              {gcalStatus.connected && <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-cyan)] text-[var(--rammy-text)] px-1.5 py-0.5 rounded-full font-semibold">Read + Write</span>}
              {gcalStatus.status === "expired" && <span className="text-[9px] uppercase tracking-widest bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded-full font-semibold">Reconnect</span>}
            </div>
            <div className="text-xs text-[var(--rammy-text-sec)] mt-1 leading-relaxed">
              {gcalStatus.connected
                ? <>Connected as <span className="font-mono">{gcalStatus.email || "your Google account"}</span>. Rammy can read events and draft new ones for your approval.</>
                : gcalStatus.status === "expired"
                ? "Your Google connection expired. Reconnect to keep briefings warm."
                : "One tap. Rammy will read your events and can draft new ones you approve before sending."}
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {!gcalStatus.connected && (
                <Button onClick={connectGoogleCalendar} disabled={gcalBusy}
                  className="btn-pill h-9 px-4 bg-[var(--rm-ink-900)] text-white hover:opacity-90"
                  data-testid="gcal-connect">
                  {gcalBusy ? <L.Loader2 size={14} className="animate-spin"/> : "Connect Google Calendar"}
                </Button>
              )}
              {gcalStatus.connected && (
                <Button onClick={disconnectGoogleCalendar} disabled={gcalBusy}
                  variant="ghost"
                  className="btn-pill h-9 px-4 border border-[var(--rammy-border)] text-[var(--rammy-text-muted)] hover:text-red-600 hover:border-red-300"
                  data-testid="gcal-disconnect">
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* iCal alternative */}
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-sm">Or paste an iCal URL</div>
            <div className="text-[11px] text-[var(--rammy-text-muted)] mt-0.5">Read-only. Works with iCloud, Outlook, Notion Calendar too.</div>
          </div>
          {user?.calendar_ical_url && <span className="text-[9px] uppercase tracking-widest bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)] px-1.5 py-0.5 rounded-full font-semibold">Read only</span>}
        </div>
        <Label htmlFor="cal-url" className="sr-only">iCal (.ics) URL</Label>
        <Input id="cal-url" placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
          value={url} onChange={e=>setUrl(e.target.value)} className="rounded-xl" data-testid="cal-url-input"/>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving} className="btn-pill btn-primary flex-1" data-testid="cal-save">
            {saving ? "Saving…" : (user?.calendar_ical_url ? "Update" : "Connect")}
          </Button>
          {user?.calendar_ical_url && (
            <Button onClick={()=>{ setUrl(""); }} variant="ghost" className="btn-pill border border-[var(--rammy-border)]" data-testid="cal-clear">Clear</Button>
          )}
        </div>
      </div>

      {user?.calendar_ical_url && (
        <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white p-4">
          <div className="text-xs uppercase tracking-widest text-white/60">Your day, told warmly</div>
          {brief ? (
            <div className="mt-2 text-sm leading-relaxed" data-testid="cal-brief-text">{brief}</div>
          ) : (
            <div className="mt-2 text-sm text-white/70">Tap below and Rammy will read the shape of your day out loud.</div>
          )}
          <Button onClick={doBrief} disabled={briefBusy} className="btn-pill btn-primary mt-3 w-full" data-testid="cal-brief-btn">
            {briefBusy ? "Reading your day…" : "Brief my day"}
          </Button>
        </div>
      )}

      {loading && <div className="text-sm text-[var(--rammy-text-muted)] text-center py-6">Reading your calendar…</div>}
      {!loading && user?.calendar_ical_url && events.length === 0 && (
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 text-center text-sm text-[var(--rammy-text-muted)]" data-testid="cal-empty">
          Nothing on the horizon this week. Beautiful.
        </div>
      )}
      {groups.map(g => (
        <div key={g.label}>
          <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold px-1 mb-2" data-testid={`cal-day-${g.key}`}>{g.label}</div>
          <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] divide-y divide-[var(--rammy-border)] overflow-hidden">
            {g.items.map((e, i) => (
              <div key={i} className="p-3 flex gap-3">
                <div className="w-14 shrink-0 text-xs font-mono text-[var(--rammy-cyan-dark)] mt-0.5">
                  {e.all_day ? "all day" : new Date(e.start).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{e.title}</div>
                  {e.location && <div className="text-xs text-[var(--rammy-text-muted)] flex items-center gap-1 mt-0.5"><L.MapPin size={11}/>{e.location}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function groupByDay(events) {
  const out = new Map();
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const label = (d) => {
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = (day - today) / 86400000;
    if (diff === 0) return { key: "today", label: "Today" };
    if (diff === 1) return { key: "tomorrow", label: "Tomorrow" };
    return { key: day.toISOString().slice(0,10), label: day.toLocaleDateString(undefined,{weekday:"long", month:"short", day:"numeric"}) };
  };
  for (const ev of events) {
    const d = new Date(ev.start);
    const { key, label: lab } = label(d);
    if (!out.has(key)) out.set(key, { key, label: lab, items: [] });
    out.get(key).items.push(ev);
  }
  return Array.from(out.values());
}
